/* eslint-disable */
import React, { useCallback, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Typography } from 'tfc-components';

import CameraCapture, { CameraCaptureRef } from 'components/templates/CameraCapture';
import MainLayout from 'components/templates/MainLayout';
import { checkinQrcodeServiceV2, confirmQrcodeServiceV2 } from 'services/checkin';

type ScanState = 'idle' | 'scanning' | 'checkin-success' | 'confirmed' | 'info' | 'error';

const ScanQRCode: React.FC = () => {
  const carmeraRef = useRef<CameraCaptureRef>(null);
  const [scanState, setScanState] = useState<ScanState>('idle');
  // const [checkinData, setcheckinData] = useState<CheckInvitationData | null>(null);
  const [errorMsg, setErrorMsg] = useState<React.ReactNode>();

  const {
    data: checkinData,
    mutate: checkinMutate,
    isLoading
  } = useMutation(
    ['verifyQRCode'],
    (qrCode: string) => checkinQrcodeServiceV2(qrCode),
    {
      onSuccess: (data) => {
        if (data.isCheckin) {
          setScanState('confirmed');
        } else {
          setScanState('info');
        }
      },
      onError: (error: any) => {
        const errorRes = (error as any)?.response?.data?.errors;
        if (errorRes && errorRes.length > 0) {
          const errorCode = errorRes[0]?.code;
          if (errorCode === 'CAMPAIGN_NOT_FOUND') {
            setScanState('error');
            setErrorMsg(
              <>
                Chưa có chương trình diễn ra
              </>
            );
            return;
          }
        }

        setScanState('error');
        setErrorMsg(
          <>
            Mã QR check-in của Quý nhân viên y tế
            <br />
            không hợp lệ
            <br />
            Vui lòng kiểm tra lại mã QR hoặc
            <br />
            liên hệ với nhân viên Abbott để được
            <br />
            hỗ trợ.
          </>
        );

        // setScanState('info');
      }
    }
  );

  // dummy data checkin checkinData
  // const checkinData = {
  //   fullName: 'Nguyễn Văn A',
  //   phone: '0123456789',
  //   positionOrDegree: 'Bác sĩ',
  //   hospital: 'Bệnh viện Bạch Mai',
  //   department: 'Khoa Ngoại',
  //   province: {
  //     code: '1',
  //     name: 'Hà Nội'
  //   },
  //   signature: '123',
  //   createdAt: '2022-01-01',
  //   updatedAt: '2022-01-01',
  //   checkinAt: '2022-01-01',
  //   isCheckin: false,
  //   isReceiveSample: false,
  //   isNewCheckin: true,
  //   routeCode: '123',
  // };

  const {
    mutate: confirmMutate,
    isLoading: confirmLoading,
    data: confirmData,
  } = useMutation(
    ['confirmQRCode'],
    (qrCode: string) => confirmQrcodeServiceV2(qrCode),
    {
      onSuccess: () => {
        setScanState('checkin-success');
      },
      onError: () => {
        setErrorMsg('Xác nhận thất bại');
        setScanState('error');
      },
    }
  );

  const handleQRScan = useCallback((data: string) => {
    if (!data) return;
    setErrorMsg('');
    checkinMutate(data);
  }, [checkinMutate]);

  const handleReset = () => {
    setScanState('idle');
    setErrorMsg('');
  };

  const handleConfirm = () => {
    if (checkinData?.routeCode) {
      confirmMutate(checkinData?.routeCode);
    }
  };

  return (
    <MainLayout mode="scan">
      <div className="p-scanQRCode">
        {/* ── IDLE / SCAN BUTTON ── */}
        {(scanState === 'idle' || scanState === 'scanning') && (
          <div className="p-scanQRCode_scanner">
            {/* Animated camera frame — overlays (corners, scan-line, icon) are rendered by CameraCapture */}
            <div className="p-scanQRCode_frame">
              <CameraCapture
                ref={carmeraRef}
                frameMode
                isSingle
                name="qrcode"
                handleQRCode={handleQRScan}
              />
            </div>

            <Typography.Text
              textStyle="center"
              extendClasses="fs-26x36 p-scanQRCode_hint"
            >
              {isLoading ? 'Đang xử lý...' : 'Đặt mã QR vào khung để quét'}
            </Typography.Text>
          </div>
        )}

        {/* ── CONFIRMED (PG already scanned) ── */}
        {scanState === 'checkin-success' && (
          <div className="p-scanQRCode_result p-scanQRCode_result--confirmed">
            <div className="p-scanQRCode_result-icon p-scanQRCode_result-icon--success">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#27ae60" strokeWidth="2" />
                <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#27ae60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <Typography.Text
              textStyle="center"
              fontweight="700"
              extendClasses="fs-36x44 p-scanQRCode_result-title p-scanQRCode_result-title--success"
            >
              Check-in thành công
            </Typography.Text>
            <div className="p-scanQRCode_infoCard">
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Họ và tên:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {confirmData?.fullName || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  SĐT:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {confirmData?.phone || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Bệnh viện:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {confirmData?.hospital || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Tỉnh/TP:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {confirmData?.province?.name || ''}
                </Typography.Text>
              </div>
              {/* <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Khu vực:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {confirmData?.group || ''}
                </Typography.Text>
              </div> */}
            </div>
            {/* <Typography.Text
              textStyle="center"
              extendClasses="fs-22x32 p-scanQRCode_result-desc"
            >
              PG đã quét mã QR này trước đó.
              {'\n'}
              Thông tin đã được xác nhận.
            </Typography.Text> */}
            <button
              id="btn-scan-qr-again-confirmed"
              className="p-scanQRCode_resetBtn"
              onClick={handleReset}
            >
              Về trang chủ
            </button>
          </div>
        )}

        {scanState === 'confirmed' && (
          <div className="p-scanQRCode_result p-scanQRCode_result--error">
            <div className="p-scanQRCode_result-icon p-scanQRCode_result-icon--error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#e74c3c" strokeWidth="2" />
                <line x1="9" y1="9" x2="15" y2="15" stroke="#e74c3c" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="15" y1="9" x2="9" y2="15" stroke="#e74c3c" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <Typography.Text
              textStyle="center"
              fontweight="700"
              extendClasses="fs-36x44 p-scanQRCode_result-title p-scanQRCode_result-title--error"
            >
              Mã QR đã check-in
            </Typography.Text>
            {/* <Typography.Text
              textStyle="center"
              extendClasses="fs-22x32 p-scanQRCode_result-desc"
            >
              PG đã quét mã QR này trước đó.
              {'\n'}
              Thông tin đã được xác nhận.
            </Typography.Text> */}
            <button
              id="btn-scan-qr-again-confirmed"
              className="p-scanQRCode_resetBtn"
              onClick={handleReset}
            >
              Về trang chủ
            </button>
          </div>
        )}

        {/* ── INFO (show member info) ── */}
        {scanState === 'info' && checkinData && (
          <>
            {/* <div className="p-scanQRCode_result-icon p-scanQRCode_result-icon--info">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#f39c12" strokeWidth="2" />
                <circle cx="12" cy="8" r="1" fill="#f39c12" />
                <line x1="12" y1="11" x2="12" y2="17" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div> */}
            {/* <Typography.Text
              textStyle="center"
              fontweight="700"
              extendClasses="fs-28x40 p-scanQRCode_result-title p-scanQRCode_result-title--info"
            >
              THÔNG TIN ĐẠI BIỂU
            </Typography.Text> */}

            <div className="p-scanQRCode_infoCard">
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Họ và tên:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {checkinData.fullName || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  SĐT:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {checkinData.phone || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Bệnh viện:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {checkinData.hospital || ''}
                </Typography.Text>
              </div>
              <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Tỉnh/TP:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {checkinData.province?.name || ''}
                </Typography.Text>
              </div>
              {/* <div className="p-scanQRCode_infoRow">
                <Typography.Text
                  fontweight="700"
                  extendClasses="fs-26x36 p-scanQRCode_infoLabel"
                >
                  Khu vực:
                </Typography.Text>
                <Typography.Text
                  extendClasses="fs-26x36 p-scanQRCode_infoValue"
                >
                  {checkinData.group || ''}
                </Typography.Text>
              </div> */}
            </div>
            <div className="p-scanQRCode_buttonGroup">
              <button
                id="btn-scan-qr-again-info"
                className="p-scanQRCode_resetBtn"
                onClick={handleReset}
                disabled={confirmLoading}
              >
                Về trang chủ
              </button>
              <button
                id="btn-scan-qr-confirm"
                className="p-scanQRCode_resetBtn"
                onClick={handleConfirm}
                disabled={confirmLoading}
              >
                Check-in
              </button>
            </div>
          </>
        )}

        {/* ── ERROR ── */}
        {scanState === 'error' && (
          <div className="p-scanQRCode_result p-scanQRCode_result--error">
            <div className="p-scanQRCode_result-icon p-scanQRCode_result-icon--error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#e74c3c" strokeWidth="2" />
                <line x1="9" y1="9" x2="15" y2="15" stroke="#e74c3c" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="15" y1="9" x2="9" y2="15" stroke="#e74c3c" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            {/* <Typography.Text
              textStyle="center"
              fontweight="700"
              extendClasses="fs-36x44 p-scanQRCode_result-title p-scanQRCode_result-title--error"
            >
              LỖI
            </Typography.Text> */}
            <Typography.Text
              textStyle="center"
              extendClasses="fs-26x36 p-scanQRCode_result-desc"
            >
              {errorMsg}
            </Typography.Text>
            <button
              id="btn-scan-qr-retry"
              className="p-scanQRCode_resetBtn"
              onClick={handleReset}
            >
              Thử lại
            </button>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default ScanQRCode;
